/**
 * NotifContext — lightweight "what just happened" notifications for the
 * ActionRail / MobileBottomBar.
 *
 * Two concerns in one place:
 *  1. Persistent badge state per action (`pending`): when the Auto-IA pipeline
 *     generates a lettre de motivation, the Lettre action displays a dot
 *     until the user opens that modal and consumes it.
 *  2. One-shot "fly" animations: when an action becomes pending, we can spawn
 *     a floating icon that animates from a source rect (usually the adapt
 *     modal center) to the ActionRail button, so the user *sees* where the
 *     result landed — otherwise the output would be invisible.
 *
 * Consumers:
 *  - SimpleActionRail / MobileBottomBar register their button DOM nodes via
 *    `registerTarget(action, el)` so the overlay knows the flight destination.
 *  - AdaptToJobModal calls `mark('letter', fromRect)` right before closing.
 *  - The same buttons call `clear(action)` when clicked so the badge goes away.
 */

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import { createPortal } from 'react-dom';
import { Mail, Globe, ChartNoAxesCombined, WandSparkles, type LucideIcon } from 'lucide-react';

export type NotifAction = 'letter' | 'score' | 'translate' | 'adapt';

// eslint-disable-next-line react-refresh/only-export-components
export const NOTIF_ICONS: Record<NotifAction, LucideIcon> = {
  letter: Mail,
  score: ChartNoAxesCombined,
  translate: Globe,
  adapt: WandSparkles,
};

// eslint-disable-next-line react-refresh/only-export-components
export const NOTIF_COLORS: Record<NotifAction, string> = {
  letter: '#c4b5fd',    // violet-300
  score: '#6ee7b7',     // emerald-300
  translate: '#67e8f9', // cyan-300
  adapt: '#a5b4fc',     // indigo-300
};

interface Flight {
  id: string;
  action: NotifAction;
  from: { x: number; y: number };
  to: { x: number; y: number };
}

interface NotifContextValue {
  pending: Record<NotifAction, boolean>;
  mark: (action: NotifAction, fromRect?: DOMRect | { left: number; top: number; width: number; height: number } | null) => void;
  clear: (action: NotifAction) => void;
  registerTarget: (action: NotifAction, el: HTMLElement | null) => void;
}

const NotifContext = createContext<NotifContextValue | null>(null);

const INITIAL: Record<NotifAction, boolean> = { letter: false, score: false, translate: false, adapt: false };

export function NotifProvider({ children }: { children: ReactNode }) {
  const [pending, setPending] = useState<Record<NotifAction, boolean>>(INITIAL);
  const [flights, setFlights] = useState<Flight[]>([]);
  const targetsRef = useRef<Partial<Record<NotifAction, HTMLElement>>>({});

  const registerTarget = useCallback((action: NotifAction, el: HTMLElement | null) => {
    if (el) targetsRef.current[action] = el;
    else delete targetsRef.current[action];
  }, []);

  const mark = useCallback<NotifContextValue['mark']>((action, fromRect) => {
    setPending((prev) => ({ ...prev, [action]: true }));
    const target = targetsRef.current[action];
    if (!target) return;
    const to = target.getBoundingClientRect();
    const src = fromRect ?? {
      left: window.innerWidth / 2 - 30,
      top: window.innerHeight / 2 - 30,
      width: 60,
      height: 60,
    };
    const flight: Flight = {
      id: `${action}-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`,
      action,
      from: { x: src.left + src.width / 2, y: src.top + src.height / 2 },
      to: { x: to.left + to.width / 2, y: to.top + to.height / 2 },
    };
    setFlights((f) => [...f, flight]);
    // Flight lasts ~800ms; remove a bit after to let the final fade finish.
    window.setTimeout(() => {
      setFlights((f) => f.filter((x) => x.id !== flight.id));
    }, 1000);
  }, []);

  const clear = useCallback((action: NotifAction) => {
    setPending((prev) => (prev[action] ? { ...prev, [action]: false } : prev));
  }, []);

  const value = useMemo<NotifContextValue>(() => ({ pending, mark, clear, registerTarget }), [pending, mark, clear, registerTarget]);

  return (
    <NotifContext.Provider value={value}>
      {children}
      <FlyOverlay flights={flights} />
    </NotifContext.Provider>
  );
}

// eslint-disable-next-line react-refresh/only-export-components
export function useNotif(): NotifContextValue {
  const ctx = useContext(NotifContext);
  if (!ctx) throw new Error('useNotif must be used within <NotifProvider>');
  return ctx;
}

/** Overlay rendering currently-in-flight icons. */
function FlyOverlay({ flights }: { flights: Flight[] }) {
  if (typeof document === 'undefined') return null;
  if (flights.length === 0) return null;
  return createPortal(
    <div className="fixed inset-0 pointer-events-none z-[10100]" aria-hidden="true">
      {flights.map((f) => (
        <FlyingIcon key={f.id} flight={f} />
      ))}
    </div>,
    document.body,
  );
}

function FlyingIcon({ flight }: { flight: Flight }) {
  const [phase, setPhase] = useState<'start' | 'end'>('start');
  useEffect(() => {
    // rAF to ensure the "start" frame commits before transitioning
    const id = requestAnimationFrame(() => {
      requestAnimationFrame(() => setPhase('end'));
    });
    return () => cancelAnimationFrame(id);
  }, []);

  const Icon = NOTIF_ICONS[flight.action];
  const color = NOTIF_COLORS[flight.action];

  const isEnd = phase === 'end';
  const size = isEnd ? 22 : 56;
  const x = isEnd ? flight.to.x : flight.from.x;
  const y = isEnd ? flight.to.y : flight.from.y;

  return (
    <div
      style={{
        position: 'fixed',
        left: x - size / 2,
        top: y - size / 2,
        width: size,
        height: size,
        transition: 'left 780ms cubic-bezier(.55, 0, .1, 1), top 780ms cubic-bezier(.55, 0, .1, 1), width 780ms cubic-bezier(.55, 0, .1, 1), height 780ms cubic-bezier(.55, 0, .1, 1), opacity 780ms ease-out, transform 780ms cubic-bezier(.55, 0, .1, 1)',
        transform: isEnd ? 'rotate(10deg) scale(0.85)' : 'rotate(-6deg) scale(1)',
        opacity: isEnd ? 0 : 1,
        background: `radial-gradient(circle at 30% 30%, ${color}, transparent 70%)`,
        borderRadius: '50%',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        boxShadow: `0 8px 30px ${color}55, 0 0 0 1px ${color}33`,
        willChange: 'left, top, width, height, transform, opacity',
      }}
    >
      <Icon size={Math.max(14, size * 0.55)} color="#0f172a" strokeWidth={2.2} />
    </div>
  );
}
